import React from 'react'
import moment from 'moment'
import type { Tick } from './index'

const TOOLTIP_WIDTH = 280
const TOOLTIP_X_OFFSET = 12
const TOOLTIP_Y = 12
const TOOLTIP_LINE_HEIGHT = 22
const TOOLTIP_PADDING_X = 14
const TOOLTIP_PADDING_Y = 12
const TOOLTIP_EDGE_GAP = 24

export type BarChartTooltipRow = {
  ts: string,
  label: string,
  value: number,
  unit: string,
  decimals?: number,
}

type HoveredSlot = {
  ts: string,
  x: number,
}

type TooltipPlacement = 'top' | 'bottom'

type Props = {
  hoveredSlot: HoveredSlot | null,
  rows: BarChartTooltipRow[],
  svgWidth: number,
  svgHeight?: number,
  tick?: Tick,
  placement?: TooltipPlacement,
}

const BarChartTooltip = ({
  hoveredSlot,
  rows,
  svgWidth,
  svgHeight,
  tick,
  placement = 'top',
}: Props): JSX.Element | null => {
  if (!hoveredSlot || rows.length === 0) return null

  const dateFormat = tick?.hoverFormatExtended || tick?.hoverFormat || 'DD.MM.YYYY'

  const tooltipHeight = rows.length * TOOLTIP_LINE_HEIGHT + TOOLTIP_PADDING_Y * 2

  const tooltipX = Math.min(
    Math.max(8, hoveredSlot.x + TOOLTIP_X_OFFSET),
    svgWidth - TOOLTIP_WIDTH - 8
  )

  const tooltipY =
    placement === 'bottom' && svgHeight !== undefined
      ? Math.max(TOOLTIP_EDGE_GAP, svgHeight - tooltipHeight - TOOLTIP_EDGE_GAP)
      : TOOLTIP_Y

  const formatValue = (value: number, decimals = 2): string => {
    return Number(value || 0).toFixed(decimals)
  }

  return (
    <g className="combined-tooltip-wrapper">
      <rect
        className="combined-tooltip-bg"
        x={tooltipX}
        y={tooltipY}
        width={TOOLTIP_WIDTH}
        height={tooltipHeight}
        rx={6}
        ry={6}
      />

      {rows.map((row, index) => (
        <text
          key={`${row.label}-${row.ts}-${index}`}
          className="combined-tooltip-text"
          textAnchor="start"
          x={tooltipX + TOOLTIP_PADDING_X}
          y={tooltipY + TOOLTIP_PADDING_Y + 16 + index * TOOLTIP_LINE_HEIGHT}
        >
          {moment(row.ts).format(dateFormat)} {row.label}: {formatValue(row.value, row.decimals)} {row.unit}
        </text>
      ))}
    </g>
  )
}

export default BarChartTooltip